import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiSearch, FiX, FiBook, FiHash, FiZap } from "react-icons/fi";
import { searchIndex, SearchResult } from "./searchIndex";

// --- Helper: Highlighted Text ---
const HighlightedText = ({ text, query }: { text: string; query: string }) => {
  if (!query.trim()) return <>{text}</>;
  
  const tokens = query.toLowerCase().split(/\s+/).filter(t => t.length > 0);
  const regex = new RegExp(`(${tokens.join("|")})`, "gi");
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) => (
        regex.test(part) ? (
          <mark key={i} className="bg-blue-100 text-blue-700 rounded-px px-0.5 font-bold">
            {part}
          </mark>
        ) : (
          part
        )
      ))}
    </>
  );
};

const DocsSearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // --- Popular Searches ---
  const popularTopics = [
    { title: "One-off Programs", href: "/dashboard/onboarding-documentation/scheduled-runs#run-types" },
    { title: "Snapshot Verification", href: "/dashboard/onboarding-documentation/snapshot-verification" },
    { id: "step4-main", title: "Credential Vault", href: "/dashboard/onboarding-documentation/credential-vault" },
    { id: "step2-main", title: "Target Scoping", href: "/dashboard/onboarding-documentation/scoping" },
  ];

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // --- Click Outside Detection ---
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // --- Helper: Contextual Snippet (VS Code Style) ---
  const getSnippet = (text: string, query: string) => {
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const tokens = lowerQuery.split(/\s+/).filter(t => t.length > 0);
    
    // Find first match of any token or the full query
    let matchIndex = lowerText.indexOf(lowerQuery);
    if (matchIndex === -1 && tokens.length > 0) {
      matchIndex = lowerText.indexOf(tokens[0]);
    }
    
    if (matchIndex === -1) return text.slice(0, 140) + "...";

    const start = Math.max(0, matchIndex - 60);
    const end = Math.min(text.length, matchIndex + 80);
    let snippet = text.slice(start, end);
    
    if (start > 0) snippet = "..." + snippet;
    if (end < text.length) snippet = snippet + "...";
    
    return snippet;
  };

  // --- Helper: Simple Fuzzy Match (Typos) ---
  const fuzzyMatch = (target: string, query: string) => {
    if (target.includes(query)) return 1;
    // Basic edit distance check for short words (allow 1 char diff)
    if (query.length > 4) {
      // Very simple: check if query minus one char is in target
      for (let i = 0; i < query.length; i++) {
        const sub = query.slice(0, i) + query.slice(i + 1);
        if (target.includes(sub)) return 0.7;
      }
    }
    return 0;
  };

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    const searchTokens = query.toLowerCase().split(/\s+/).filter(t => t.length > 0);
    const lowerQuery = query.toLowerCase();
    
    const scoredResults = searchIndex
      .map(item => {
        let score = 0;
        const titleLower = item.title.toLowerCase();
        const contentLower = item.content.toLowerCase();
        const keywordsLower = item.keywords?.join(" ").toLowerCase() || "";
        const categoryLower = item.category.toLowerCase();
        const fullText = `${titleLower} ${contentLower} ${categoryLower} ${keywordsLower}`;
        
        // 1. EXACT PHRASE MATCH (VS Code Style) - Massive Boost
        if (fullText.includes(lowerQuery)) {
          score += 1000; // Increased boost
          if (titleLower.includes(lowerQuery)) score += 500;
        } else {
          // 1b. Fuzzy Phrase Match
          const fuzzyScore = fuzzyMatch(fullText, lowerQuery);
          if (fuzzyScore > 0) score += (fuzzyScore * 400);
        }
        
        // 2. TOKEN MATCHING
        const matchedTokens = searchTokens.filter(token => {
          if (fullText.includes(token)) return true;
          return fuzzyMatch(fullText, token) > 0;
        });

        if (matchedTokens.length === 0 && score === 0) return { ...item, score: -1 };

        // Completeness Boost
        if (matchedTokens.length === searchTokens.length) score += 300;
        
        // Density Boost
        score += (matchedTokens.length * 100);

        // Location Weights
        matchedTokens.forEach(token => {
          if (titleLower.includes(token)) score += 100;
          if (keywordsLower.includes(token)) score += 50;
          if (contentLower.includes(token)) score += 20;
          
          // Fuzzy per-token boost
          if (!fullText.includes(token)) score += 15; // Small boost for fuzzy match
        });

        // 3. SECONARY CONTEXT: Extract the best snippet
        const snippet = getSnippet(item.content, query);

        return { ...item, score, snippet };
      })
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);

    setResults(scoredResults as any);
    setSelectedIndex(0);
    setIsOpen(true);
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (results.length === 0) return;
    if (e.key === "ArrowDown") {
      setSelectedIndex(prev => (prev + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      setSelectedIndex(prev => (prev - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      const selected = results[selectedIndex];
      if (selected) {
        const hParam = `h=${encodeURIComponent(query)}`;
        const separator = selected.href.includes("?") ? "&" : "?";
        // Handle hash placement: path?h=query#hash
        let finalUrl = selected.href;
        if (selected.href.includes("#")) {
          const [path, hash] = selected.href.split("#");
          finalUrl = `${path}${path.includes("?") ? "&" : "?"}${hParam}#${hash}`;
        } else {
          finalUrl = `${selected.href}${separator}${hParam}`;
        }
        
        navigate(finalUrl);
        setIsOpen(false);
        setQuery("");
      }
    }
  };

  // Group results by category
  const categories = Array.from(new Set(results.map(r => r.category)));

  return (
    <div ref={searchRef} className="relative w-full max-w-sm md:max-w-md">
      <div className="relative group">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
        <input 
          ref={inputRef}
          type="text" 
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder="Search documentation (Cmd+K)"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-12 text-sm outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all shadow-sm"
        />
        
        {query && (
          <button 
            onClick={() => { setQuery(""); setIsOpen(false); }}
            className="absolute right-12 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900 transition-colors"
          >
            <FiX size={16} />
          </button>
        )}

        <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden md:flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold text-slate-400 pointer-events-none">
          <span className="text-[12px]">⌘</span>K
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-3 max-h-[500px] overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl z-[100] p-2 custom-scrollbar animate-in fade-in slide-in-from-top-2 duration-200">
          
          {/* Default: Popular Topics */}
          {query.trim().length <= 1 && (
            <div className="p-2">
              <div className="flex items-center gap-2 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                <FiZap className="text-amber-500" /> Quick Discovery
              </div>
              <div className="grid grid-cols-1 gap-1">
                {popularTopics.map((topic, idx) => (
                  <Link
                    key={idx}
                    to={topic.href}
                    onClick={() => { setIsOpen(false); setQuery(""); }}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group no-underline"
                  >
                    <div className="p-2 bg-slate-100 rounded-lg text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                      <FiBook size={14} />
                    </div>
                    <span className="text-sm font-bold text-slate-700 group-hover:text-black">{topic.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Search Results Grouped by Category */}
          {query.trim().length > 1 && results.length > 0 && categories.map(category => (
            <div key={category} className="mb-4 last:mb-0">
              <div className="flex items-center gap-2 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1 sticky top-0 bg-white/90 backdrop-blur-sm z-10">
                <FiHash className="text-blue-400" /> {category}
              </div>
              <div className="space-y-1 px-1">
                {results.filter(r => r.category === category).map((result, idx) => {
                  const globalIdx = results.indexOf(result);
                    const hParam = `h=${encodeURIComponent(query)}`;
                    let finalUrl = result.href;
                    if (result.href.includes("#")) {
                      const [path, hash] = result.href.split("#");
                      finalUrl = `${path}${path.includes("?") ? "&" : "?"}${hParam}#${hash}`;
                    } else {
                      finalUrl = `${result.href}${result.href.includes("?") ? "&" : "?"}${hParam}`;
                    }

                    return (
                      <Link
                        key={result.id}
                        to={finalUrl}
                        onClick={() => { setIsOpen(false); setQuery(""); }}
                        className={`flex flex-col gap-1 p-3 rounded-xl transition-all no-underline ${
                          globalIdx === selectedIndex ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20" : "hover:bg-slate-50"
                        }`}
                      >
                      <span className={`text-sm font-black ${globalIdx === selectedIndex ? "text-white" : "text-black"}`}>
                        <HighlightedText text={result.title} query={query} />
                      </span>
                      <p className={`text-[11px] m-0 line-clamp-2 leading-relaxed ${globalIdx === selectedIndex ? "text-blue-100" : "text-slate-500"}`}>
                        <HighlightedText text={(result as any).snippet || result.content} query={query} />
                      </p>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          {/* No Results */}
          {query.trim().length > 1 && results.length === 0 && (
            <div className="p-10 text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiSearch size={24} className="text-slate-300" />
              </div>
              <p className="text-sm font-bold text-slate-900 m-0 mb-1">No results matching "{query}"</p>
              <p className="text-xs text-slate-500 m-0">Try searching for broader terms like "vault" or "step 1".</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocsSearch;
